/**
 * types.ts — Remyx annotation types
 *
 * Extends react-pdf-highlighter-extended's Highlight with
 * Remyx-specific fields (annotation_type, note_text, author, etc.)
 */
import type { Highlight, ScaledPosition } from "react-pdf-highlighter-extended";

export type AnnotationType = "highlight" | "note";

/**
 * Custom highlight type that carries our annotation metadata.
 * The library renders one HighlightContainer per highlight and
 * gives us access to this data via useHighlightContainerContext<RemyxHighlight>().
 */
export interface RemyxHighlight extends Highlight {
  annotation_type: AnnotationType;
  note_text?: string | null;
  author?: {
    display_name?: string;
    name?: string;
  } | null;
  experiment_id?: string | null;
}

/** Color scheme for each annotation type — matches existing design */
export const TYPE_COLORS: Record<
  AnnotationType,
  { solid: string; bg: string }
> = {
  highlight: { solid: "#FBBF24", bg: "rgba(251,191,36,0.28)" },
  note: { solid: "#60A5FA", bg: "rgba(96,165,250,0.24)" },
};

/**
 * Raw annotation shape coming from the Flask API (GET /papers/:id/annotations).
 * This is the existing backend format before conversion.
 */
export interface RawAnnotation {
  id: string;
  annotation_type: AnnotationType;
  selected_text: string;
  note_text?: string | null;
  page_number?: number;
  position_data?: LegacyPositionData | ScaledPosition | null;
  author?: { display_name?: string; name?: string } | null;
  experiment_id?: string | null;
  created_at?: string;
}

/** Legacy position format from the old paper-viewer.js */
export interface LegacyPositionData {
  rects: Array<{
    xPct: number;
    yPct: number;
    wPct: number;
    hPct: number;
    page: number;
  }>;
}

/** Check whether position_data is in the new ScaledPosition format */
export function isScaledPosition(pd: any): pd is ScaledPosition {
  return pd && typeof pd === "object" && "boundingRect" in pd;
}

/** Check whether position_data is in the old xPct/yPct format */
export function isLegacyPosition(pd: any): pd is LegacyPositionData {
  return (
    pd &&
    typeof pd === "object" &&
    Array.isArray(pd.rects) &&
    pd.rects.length > 0 &&
    typeof pd.rects[0].xPct === "number"
  );
}
